import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import { NumberInput } from "./NumberInput";
import { FactorControls } from "./FactorControls";
import type { TrendTier } from "./types";

interface SearchTrendScoringCardProps {
  searchVolumeTrendWeight: number;
  onSearchVolumeTrendWeightChange: (v: number) => void;
  trendTiers: TrendTier[];
  onTierMaxBoundChange: (idx: number, v: number) => void;
  onTierPointsChange: (idx: number, v: number) => void;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}

export function SearchTrendScoringCard({
  searchVolumeTrendWeight,
  onSearchVolumeTrendWeightChange,
  trendTiers,
  onTierMaxBoundChange,
  onTierPointsChange,
  enabled,
  onEnabledChange,
}: SearchTrendScoringCardProps) {
  return (
    <Card id="kps-module-search-trend" className={cn(!enabled && "bg-muted/30 order-1")}>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            title="Search Trend Scoring"
            description="Map search trend volume tiers to KPS point contributions."
          />
          <FactorControls
            id="kps-toggle-search-trend"
            enabled={enabled}
            onEnabledChange={onEnabledChange}
            value={searchVolumeTrendWeight}
            onValueChange={onSearchVolumeTrendWeightChange}
            step={0.5}
          />
        </div>

        <div className={cn("space-y-4", !enabled && "opacity-50 pointer-events-none select-none")}>
          <div className="space-y-0">
            <div className="grid grid-cols-[1fr_140px_120px] gap-4 items-center px-1 h-9 border-b border-border">
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Tier</span>
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide text-right">Max Bound</span>
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide text-right">Points</span>
            </div>
            {trendTiers.map((tier, i) => {
              const isLast = i === trendTiers.length - 1;
              return (
                <div key={i} className="grid grid-cols-[1fr_140px_120px] gap-4 items-center px-1 py-3 border-b border-border last:border-b-0">
                  <span className="text-sm text-foreground">
                    {tier.label}
                    {isLast && <span className="text-muted-foreground ml-1">(above tier 4)</span>}
                  </span>
                  <div className="flex justify-end">
                    <NumberInput
                      value={tier.maxBound}
                      onValueChange={(v) => onTierMaxBoundChange(i, v)}
                      min={0}
                      disabled={isLast}
                      className="w-24 h-8 text-sm text-right rounded-md bg-card tabular-nums"
                    />
                  </div>
                  <div className="flex justify-end">
                    <NumberInput
                      value={tier.points}
                      onValueChange={(v) => onTierPointsChange(i, v)}
                      step={0.5}
                      min={0}
                      max={searchVolumeTrendWeight}
                      className="w-20 h-8 text-sm text-right rounded-md bg-card tabular-nums"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
